import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit, PLATFORM_ID, SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  componentDestroyed,
  Game,
  GameStatuses,
  handleError,
  LeagueCourt, Sport,
  TournamentGamesFilters,
  UntilDestroy
} from '@mtgame/core';
import { DataSliderComponent } from '@mtgame/ui';
import { takeUntil } from 'rxjs/operators';
import { GamesSliderService } from './games-slider.service';
import { isPlatformBrowser } from '@angular/common';
import { getTourTitle } from '@utils/hardcode';

@UntilDestroy
@Component({
  selector: 'mtg-games-slider',
  templateUrl: './games-slider.component.html',
  styleUrls: ['./games-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GamesSliderService
  ]
})
export class GamesSliderComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  sliderTitle: string;
  @Input()
  leagueId: number;
  @Input()
  seasonId: number;
  @Input()
  tournamentId: number;
  @Input()
  tours: number[];
  @Input()
  currentTour: number;
  @Input()
  court: LeagueCourt;
  @Input()
  statuses: GameStatuses[];
  @Input()
  sport: Sport;
  @Input()
  roundId: number;
  @Input()
  divisionId: number;
  @Input()
  stageId: number;
  games: Game[];
  loading: boolean;
  @ViewChild('dataSliderComponent', {read: DataSliderComponent})
  dataSliderComponent: DataSliderComponent;
  isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private gamesSliderService: GamesSliderService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ((changes.roundId || changes.divisionId || changes.stageId) && this.games) {
      await this.gamesSliderService.setFilter({
        tournamentRoundId: this.roundId,
        divisionId: this.divisionId,
        tournamentStageId: this.stageId,
      });
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.isBrowser) {
      await this.gamesSliderService.initialize({
        leagueId: this.leagueId,
        tournamentId: this.tournamentId,
        seasonId: this.seasonId,
        filters: {
          ...(this.court ? {tournamentCourtId: this.court.id} : {}),
          statuses: this.statuses,
          tournamentRoundId: this.roundId,
          divisionId: this.divisionId,
          tournamentStageId: this.stageId
        }
      });
    }
    this.gamesSliderService.games$
      .pipe(takeUntil((componentDestroyed(this))))
      .subscribe(games => {
        this.games = games;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.gamesSliderService.dispose();
  }

  slideLeft(): void {
    if (this.dataSliderComponent) {
      this.dataSliderComponent.slideLeft();
    }
  }

  slideRight(): void {
    if (this.dataSliderComponent) {
      this.dataSliderComponent.slideRight();
    }
  }

  async setTour(tour: number): Promise<void> {
    await this.gamesSliderService.setFilter({tournamentTour: tour});
  }

  async loadNextPage(): Promise<void> {
    this.loading = true;
    setTimeout(() => {
      this.cdr.markForCheck();
    });
    try {
      await this.gamesSliderService.loadNextPage();
      this.loading = false;
    } catch (e) {
      handleError(e);
    }
  }

  getTourTitle(tour: number): string {
    return getTourTitle(tour, this.sport);
  }
}
